import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import "./filter-sheet.css";

/* ── The filter strip, and what it becomes on a phone ──────────────
   Every list page carries the same <section class="pg-filters"> above
   its table: a search box, two or three pickers, Apply and Clear. On a
   desktop that strip is a fair trade -- it costs a band of the page and
   it is always there when you want it.

   On a 360px screen the same controls stack into a full screen of form
   sitting on top of the list the reader opened the page to read. So
   below 768px the strip collapses to one button and the controls move
   into a sheet that rises from the bottom edge.

   Above 768px every part of the sheet is display:none and the children
   lay out exactly as they did before this component existed -- the
   wrappers restate .pg-filters' own flex column and 12px gap, so the
   spacing between the search row and the picker row is unchanged. The
   desktop strip is untouched; that was the requirement.

   The section keeps its .pg-filters class in both states, because the
   field, input, select and button styling on these pages is written as
   .pg-filters .pg-field, .pg-filters .pg-input and so on. Move the
   controls out from under that class and they lose their sizing.

   Usage -- the whole change at a call site is the tag:

     <FilterSheet label="Filter batches">
       ...the same children as before...
     </FilterSheet>
   ---------------------------------------------------------------- */

const MOBILE_QUERY = "(max-width: 767.98px)";

/* The strip only ever becomes a sheet at phone widths, so nothing here
   runs on a desktop: the trigger that sets `open` is display:none above
   768px and cannot be clicked. The one case left is a window resized
   while the sheet is up -- handled by closing it, below. */
const matchesMobile = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_QUERY).matches;

const FunnelIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="15"
    height="15"
    aria-hidden="true"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    width="18"
    height="18"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function FilterSheet({
  children,
  /* Extra classes for the strip -- br-filters and the like. They land
     on the same element they used to, so the page's own rules still
     find it. */
  className = "",
  /* The aria-label the <section> already carried. */
  label,
  /* The sheet's heading. */
  title = "Filters",
  /* The word on the button that opens it. */
  triggerLabel = "Filters",
  /* The sheet's own footer button. The page's Apply, if it has one,
     stays where it is inside the sheet -- this one only dismisses. */
  doneLabel = "Show results",
  /* How many filters are on. Optional: a page that knows can pass it
     and the shut sheet will say so, which is otherwise the one thing
     a closed sheet cannot tell the reader. */
  count,
  /* <section> by default, to match the markup this replaces; the two
     pages that used a <div> pass one. */
  as: Tag = "section",
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);
  /* Whether closing returns focus to the trigger: true for a close the
     reader asked for, false for one the window's width forced. */
  const restoreFocus = useRef(false);
  const sheetId = `${useId()}sheet`;

  const close = useCallback(() => {
    restoreFocus.current = true;
    setOpen(false);
  }, []);

  /* ── While the sheet is up ──────────────────────────────────────
     Esc shuts it, the page behind it does not scroll, and the first
     thing reachable by keyboard is the close button rather than
     whatever the browser had focused before.
     -------------------------------------------------------------- */
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    /* Rotating a phone, or dragging a desktop window narrow and back,
       would otherwise leave a fixed sheet over a page with room for the
       strip. Shut it and the strip reappears in the flow. Focus is not
       moved back to the trigger here -- the trigger is display:none at
       that width, and focusing a hidden element drops focus to body. */
    const mq = window.matchMedia(MOBILE_QUERY);
    const onWidthChange = (e) => {
      if (!e.matches) {
        restoreFocus.current = false;
        setOpen(false);
      }
    };
    /* addListener is the Safari < 14 spelling. */
    if (mq.addEventListener) mq.addEventListener("change", onWidthChange);
    else mq.addListener(onWidthChange);

    if (closeRef.current) closeRef.current.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (mq.removeEventListener) mq.removeEventListener("change", onWidthChange);
      else mq.removeListener(onWidthChange);
    };
  }, [open, close]);

  /* Focus goes back where it came from, but only for a close the reader
     asked for, and only while the trigger is still on screen. */
  useEffect(() => {
    if (open || !restoreFocus.current) return;
    restoreFocus.current = false;
    if (matchesMobile() && triggerRef.current) triggerRef.current.focus();
  }, [open]);

  /* ── The filled button shuts the sheet ──────────────────────────
     A page's own Apply is the point at which the reader wants the list
     back, so it dismisses the sheet as well as running the query.
     Clear does not: clearing is usually the first half of choosing
     something else, and it is never the filled button.

     Ten of these strips also carry an "Add user" / "Add mapping"
     button in the same row, and there the close is not a nicety: it
     opens .pg-modal, which sits at z-index 1080, and the sheet is at
     1091. A sheet left up would be lying over the dialog it opened.
     Both state changes land in one React batch, so the sheet is gone
     in the same render the dialog arrives in.

     This listens on the body rather than taking a prop on every page's
     button -- the handler on the button itself has already run by the
     time the event bubbles to here, so the filter is applied before the
     sheet goes.
     -------------------------------------------------------------- */
  const onBodyClick = useCallback(
    (e) => {
      if (!open || !e.target || typeof e.target.closest !== "function") return;
      const applied = e.target.closest(
        ".pg-filters-actions .pg-btn--main, [data-filter-apply]",
      );
      if (applied && !applied.disabled) close();
    },
    [open, close],
  );

  const hasCount = typeof count === "number" && count > 0;

  return (
    <Tag
      className={`pg-filters pgfs${className ? ` ${className}` : ""}`}
      aria-label={label}
      {...rest}
    >
      <button
        type="button"
        ref={triggerRef}
        className={`pgfs-trigger${hasCount ? " is-on" : ""}`}
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls={sheetId}
      >
        <FunnelIcon />
        <span className="pgfs-trigger-text">{triggerLabel}</span>
        {hasCount && <span className="pgfs-count">{count}</span>}
      </button>

      {open && (
        <div
          className="pgfs-scrim"
          onClick={close}
          /* The reader has the close button and Esc; this is the mouse
             shortcut, and a screen reader should not meet it at all. */
          aria-hidden="true"
        />
      )}

      <div
        id={sheetId}
        className={`pgfs-sheet${open ? " is-open" : ""}`}
        role={open ? "dialog" : undefined}
        aria-modal={open ? "true" : undefined}
        aria-label={open ? title : undefined}
      >
        <header className="pgfs-head">
          <span className="pgfs-grip" aria-hidden="true" />
          <h2 className="pgfs-title">{title}</h2>
          <button
            type="button"
            ref={closeRef}
            className="pgfs-close"
            onClick={close}
            aria-label={`Close ${title.toLowerCase()}`}
          >
            <CloseIcon />
          </button>
        </header>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions --
            not a control: it watches for a click on the page's own Apply
            button, which is a <button> and carries its own keyboard
            handling. A key handler here would fire a second time. */}
        <div className="pgfs-body" onClick={onBodyClick}>
          {children}
        </div>

        <footer className="pgfs-foot">
          <button
            type="button"
            className="pg-btn pg-btn--main pgfs-done"
            onClick={close}
          >
            {doneLabel}
          </button>
        </footer>
      </div>
    </Tag>
  );
}
